<template>
  <div>
    <v-switch
      v-model="localValue.securityEnabled"
      :disabled="actionNotAllowed"
      :ripple="false"
      class="mb-4"
      data-testid="real-time-security-switch"
      color="primary lighten-1"
      hide-details
      inset
    >
      <template #label>
        <div class="subtitle1">
          {{ $t("emailSettings.settingsTab.realTimeSecurity") }}
        </div>
      </template>
    </v-switch>
    <table-wrapper>
      <v-table class="white">
        <template #default>
          <thead>
            <tr>
              <th class="text-left caption">
                {{ $t("emailSettings.settingsTab.threatType") }}
              </th>
              <th class="text-right caption pr-7">
                {{ $t("emailSettings.settingsTab.securityMode") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="scanOption in scanOptions" :key="scanOption" class="service-row">
              <td class="content-row">
                <div class="d-flex">
                  <v-checkbox
                    v-model="localValue[scanOption].enabled"
                    :data-testid="`${scanOption}-checkbox`"
                    :ripple="false"
                    :disabled="disabled"
                  >
                    <template #label>
                      <div class="subtitle2">
                        {{ $t(`emailSettings.settingsTab.${scanOption}`) }}
                      </div>
                    </template>
                  </v-checkbox>
                </div>
              </td>
              <td class="d-flex justify-end align-center">
                <v-menu
                  v-if="
                    hasMultipleRemediationActionSupports(scanOption) &&
                    localValue[scanOption].enabled
                  "
                  min-width="auto"
                  max-width="250"
                  location="center"
                >
                  <template v-slot:activator="{ props }">
                    <v-btn
                      class="d-flex align-center remediation-btn"
                      :disabled="disabled"
                      :ripple="false"
                      v-bind="props"
                      variant="text"
                    >
                      <span class="body2">
                        {{
                          $t(
                            `emailSettings.settingsTab.securityModes.${localValue[scanOption].action}`
                          )
                        }}
                      </span>
                      <v-icon icon="$triangle" class="ml-4 item-clickable" />
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="action in remediationActions"
                      :key="action"
                      @click="setRemediation(scanOption, action)"
                    >
                      <v-list-item-title>
                        <span class="body2">
                          {{ $t(`emailSettings.settingsTab.securityModes.${action}`) }}
                        </span>
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-btn
                  v-else-if="localValue[scanOption].enabled"
                  class="d-flex align-center remediation-btn not-clickable mr-6"
                  :disabled="disabled"
                  :ripple="false"
                  variant="text"
                >
                  <span class="body2">
                    {{ $t("emailSettings.settingsTab.securityModes.warnRecipient") }}
                  </span>
                </v-btn>
                <span
                  v-else
                  class="body2 text-indigo-medium mr-10"
                  :class="{ 'text-indigo-faint': disabled }"
                >
                  {{ $t("emailSettings.settingsTab.realTimeSecurityDisabled") }}
                </span>
              </td>
            </tr>
          </tbody>
        </template>
      </v-table>
    </table-wrapper>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType, toRefs } from "vue";
import type { EmailSecuritySettings } from "@/_store/email-security/email-settings.module.ts";
import { ScanOptions, SecurityMode } from "@/constants/emails.ts";
import TableWrapper from "@/components/TableWrapper.vue";

import { useTwoWayBinding } from "@/composables/useTwoWayBinding.ts";

export default defineComponent({
  name: "RealTimeSecuritySettings",
  components: { TableWrapper },
  props: {
    modelValue: {
      type: Object as PropType<EmailSecuritySettings>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    actionNotAllowed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const { modelValue } = toRefs(props);
    const localValue = useTwoWayBinding(modelValue, (value) => {
      emit("update:modelValue", value);
    });

    const scanOptions = [
      ScanOptions.SCAN_MALWARE,
      ScanOptions.SCAN_SPAM,
      ScanOptions.SCAN_PHISHING,
      ScanOptions.SCAN_PROMPT_INJECTION,
      ScanOptions.SCAN_NEWLY_REGISTERED_DOMAINS,
      ScanOptions.SCAN_ENCRYPTED_ATTACHMENTS,
      ScanOptions.SCAN_UNVERIFIED_SENDER,
      ScanOptions.SCAN_UNKNOWN_EXTERNAL_SENDER,
    ];

    const remediationActions = [SecurityMode.QUARANTINE_EMAIL, SecurityMode.WARN_RECIPIENT];

    const hasMultipleRemediationActionSupports = (key: ScanOptions) => {
      return [
        ScanOptions.SCAN_MALWARE,
        ScanOptions.SCAN_SPAM,
        ScanOptions.SCAN_PHISHING,
        ScanOptions.SCAN_PROMPT_INJECTION,
      ].includes(key);
    };

    const setRemediation = (key: string, action: SecurityMode) => {
      switch (key) {
        case ScanOptions.SCAN_MALWARE:
          localValue.value.scanMalware.action = action;
          break;
        case ScanOptions.SCAN_SPAM:
          localValue.value.scanSpam.action = action;
          break;
        case ScanOptions.SCAN_PHISHING:
          localValue.value.scanPhishing.action = action;
          break;
        case ScanOptions.SCAN_PROMPT_INJECTION:
          localValue.value.scanPromptInjection.action = action;
          break;
      }
    };

    return {
      localValue,
      scanOptions,
      remediationActions,
      hasMultipleRemediationActionSupports,
      setRemediation,
      ScanOptions,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(*) {
  .remediation-btn {
    text-transform: initial !important;
  }
}
</style>
